import { type DifficultyChipProps, DifficultyChip } from '@exam-management/components';
import {
  type TablePaginationProps,
  Checkbox,
  Chip,
  Flex,
  FormCheckbox,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Text,
} from '@shared/components';
import { TableHeader } from '@shared/table-generator';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { QuestionNotFoundOverlay } from '../question-not-found-overlay';
import { type QuestionUsageStatusProps, QuestionUsageStatus } from '../question-usage-status';

import { singleQuestionChangeTableHeaders } from './single-question-change-table-headers';

interface FormSingleQuestionChangeTableRows {
  id: string;
  qId: string;
  difficulty: DifficultyChipProps['type'];
  calculated: boolean;
  questionStatus: QuestionUsageStatusProps;
  action: React.ReactNode;
}

export interface FormSingleQuestionChangeTableProps {
  name: string;
  rows: FormSingleQuestionChangeTableRows[];
  total: number;
  loading?: boolean;
  pagination: Pick<TablePaginationProps, 'rowsPerPage' | 'onPageChange'>;
  slots?: {
    noRowsOverlay?: React.ReactNode;
  };
}

export default function FormSingleQuestionChangeTable(
  props: Readonly<FormSingleQuestionChangeTableProps>,
) {
  const { name, rows, pagination, total, slots, loading: isLoading = false } = props;
  const { rowsPerPage, onPageChange } = pagination;

  const [page, setPage] = useState(0);

  const formContext = useFormContext();
  const { watch } = formContext;
  const value = watch(name);

  const overlayComponent = isLoading ? (
    <Loading sx={{ m: (theme) => theme.spacing(6) }} />
  ) : (
    (slots?.noRowsOverlay ?? <QuestionNotFoundOverlay />)
  );

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        borderRadius: theme.shape.rounded(2),
        border: `1px solid ${theme.palette.text.primary}`,
        overflow: 'hidden',
      })}
    >
      <TableContainer>
        <Table>
          <TableHeader
            columns={singleQuestionChangeTableHeaders}
            slotProps={{
              tableHeaderRow: {
                sx: (theme) => ({
                  '& > :not(th:first-of-type)': {
                    borderLeft: `1px solid ${theme.palette.grey[300]}`,
                  },
                }),
              },
              tableHeaderCell: {
                sx: (theme) => ({
                  ...theme.typography.body1Semibold,
                  color: theme.palette.common.white,
                  backgroundColor: theme.palette.grey[800],
                  p: theme.spacing(3),
                }),
              },
            }}
          />
          <TableBody
            sx={{
              '& > :not(tr:last-of-type) td': {
                borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
              },
            }}
          >
            {rows.map((row) => {
              return (
                <TableRow
                  key={row.qId}
                  sx={(theme) => ({
                    '& > td': {
                      p: theme.spacing(3),
                    },
                    '& > :not(td:first-of-type)': {
                      borderLeft: `1px solid ${theme.palette.grey[300]}`,
                      p: theme.spacing(2, 3),
                    },
                    backgroundColor: row.questionStatus.used
                      ? theme.palette.grey[100]
                      : theme.palette.background.paper,
                  })}
                >
                  <TableCell align='center'>
                    {!row.questionStatus.used ? (
                      <FormCheckbox
                        name={name}
                        checked={value === row.id}
                        onChange={(_, checked) => {
                          formContext.setValue(name, checked ? row.id : undefined, {
                            shouldDirty: true,
                          });
                        }}
                        sx={{ p: (theme) => theme.spacing(0) }}
                      />
                    ) : (
                      <Checkbox checked disabled sx={{ p: (theme) => theme.spacing(0) }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Text variant='body2Semibold'>{row.qId}</Text>
                  </TableCell>
                  <TableCell>
                    <DifficultyChip type={row.difficulty} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        <Text variant='captionSemibold'>
                          {row.calculated ? 'คำนวณ' : 'ไม่คำนวณ'}
                        </Text>
                      }
                      size='small'
                      sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}
                    />
                  </TableCell>
                  <TableCell>
                    <QuestionUsageStatus {...row.questionStatus} />
                  </TableCell>
                  <TableCell align='center'>{row.action}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {total > 0 ? (
        <Flex
          sx={(theme) => ({
            p: theme.spacing(3, 4),
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: `1px solid ${theme.palette.grey[300]}`,
          })}
        >
          <Text color='secondary'>{total} รายการ</Text>
          <TablePagination
            component='div'
            sx={{
              '& > .MuiToolbar-root': {
                minHeight: '32px',
                '& > *': {
                  my: 0,
                },
              },
            }}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
            count={total}
            onPageChange={(event, newPage) => {
              setPage(newPage);
              onPageChange(event, newPage);
            }}
          />
        </Flex>
      ) : (
        overlayComponent
      )}
    </Flex>
  );
}
